import { useState, useEffect } from 'react';
import { FiSliders, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const CATEGORIES = [
  { value: '',             label: 'All Categories' },
  { value: 'shirts',       label: 'Shirts' },
  { value: 'pants',        label: 'Pants' },
  { value: 'baby-clothes', label: 'Baby Clothes' },
  { value: 'kids-clothes', label: 'Kids' },
  { value: 'teen-clothes', label: 'Teens' },
];

const AGE_GROUPS = [
  { value: '',        label: 'All Ages' },
  { value: 'infant',  label: 'Infant (0–2)' },
  { value: 'toddler', label: 'Toddler (2–4)' },
  { value: 'kids',    label: 'Kids (4–12)' },
  { value: 'teen',    label: 'Teen (13–17)' },
  { value: 'adult',   label: 'Adult' },
];

const GENDERS = [
  { value: '',        label: 'All' },
  { value: 'male',    label: 'Male' },
  { value: 'female',  label: 'Female' },
  { value: 'unisex',  label: 'Unisex' },
];

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '0-3m', '3-6m', '6-12m', '1-2y', '2-3y', '3-5y', '5-8y', '8-12y'];

const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest first' },
  { value: 'price-asc',  label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'popular',    label: 'Most popular' },
  { value: 'rating',     label: 'Highest rated' },
];

function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 pb-4 mb-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full text-sm font-semibold text-yamass-black mb-3"
      >
        {title}
        {open ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
      </button>
      {open && children}
    </div>
  );
}

export default function FilterSidebar({ filters, onChange, priceRange, onClose, isMobile }) {
  const [localPrice, setLocalPrice] = useState([
    filters.minPrice || priceRange.min,
    filters.maxPrice || priceRange.max,
  ]);

  useEffect(() => {
    setLocalPrice([filters.minPrice || priceRange.min, filters.maxPrice || priceRange.max]);
  }, [priceRange, filters.minPrice, filters.maxPrice]);

  const set = (key, value) => onChange({ ...filters, [key]: value });

  const toggleSize = (size) => {
    const current = filters.sizes ? filters.sizes.split(',').filter(Boolean) : [];
    const updated  = current.includes(size)
      ? current.filter(s => s !== size)
      : [...current, size];
    onChange({ ...filters, sizes: updated.join(',') });
  };

  const selectedSizes = filters.sizes ? filters.sizes.split(',').filter(Boolean) : [];

  const clearAll = () => onChange({ category: '', ageGroup: '', gender: '', minPrice: '', maxPrice: '', sizes: '', sort: 'newest', q: filters.q || '' });

  const hasActiveFilters = filters.category || filters.ageGroup || filters.gender || filters.minPrice || filters.maxPrice || filters.sizes;

  return (
    <div className={`bg-white rounded-2xl p-5 ${isMobile ? '' : 'sticky top-24'}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <FiSliders size={16} className="text-yamass-accent" />
          <span className="font-semibold text-yamass-black text-sm">Filters</span>
          {hasActiveFilters && (
            <span className="bg-yamass-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {[filters.category, filters.ageGroup, filters.gender, filters.sizes, (filters.minPrice || filters.maxPrice)].filter(Boolean).length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button onClick={clearAll} className="text-xs text-yamass-red hover:underline">Clear all</button>
          )}
          {isMobile && onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-yamass-black"><FiX size={18} /></button>
          )}
        </div>
      </div>

      {/* Sort */}
      <FilterSection title="Sort by">
        <div className="space-y-1">
          {SORT_OPTIONS.map(o => (
            <label key={o.value} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="sort"
                value={o.value}
                checked={filters.sort === o.value}
                onChange={() => set('sort', o.value)}
                className="accent-yamass-accent"
              />
              <span className="text-sm text-gray-600 group-hover:text-yamass-black">{o.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Category */}
      <FilterSection title="Category">
        <div className="space-y-1">
          {CATEGORIES.map(c => (
            <label key={c.value} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="category"
                value={c.value}
                checked={filters.category === c.value}
                onChange={() => set('category', c.value)}
                className="accent-yamass-accent"
              />
              <span className="text-sm text-gray-600 group-hover:text-yamass-black">{c.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Age group */}
      <FilterSection title="Age group" defaultOpen={false}>
        <div className="space-y-1">
          {AGE_GROUPS.map(a => (
            <label key={a.value} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="ageGroup"
                value={a.value}
                checked={filters.ageGroup === a.value}
                onChange={() => set('ageGroup', a.value)}
                className="accent-yamass-accent"
              />
              <span className="text-sm text-gray-600 group-hover:text-yamass-black">{a.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Gender */}
      <FilterSection title="Gender" defaultOpen={false}>
        <div className="flex flex-wrap gap-2">
          {GENDERS.map(g => (
            <button
              key={g.value}
              onClick={() => set('gender', g.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                filters.gender === g.value
                  ? 'bg-yamass-black text-white border-yamass-black'
                  : 'border-gray-200 text-gray-600 hover:border-yamass-accent'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Size */}
      <FilterSection title="Size" defaultOpen={false}>
        <div className="flex flex-wrap gap-1.5">
          {SIZES.map(size => (
            <button
              key={size}
              onClick={() => toggleSize(size)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                selectedSizes.includes(size)
                  ? 'bg-yamass-black text-white border-yamass-black'
                  : 'border-gray-200 text-gray-600 hover:border-yamass-accent'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Price range */}
      <FilterSection title="Price range (৳)" defaultOpen={false}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={localPrice[0]}
              onChange={e => setLocalPrice([Number(e.target.value), localPrice[1]])}
              onBlur={() => set('minPrice', localPrice[0])}
              placeholder="Min"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-yamass-accent"
            />
            <span className="text-gray-400 text-xs flex-shrink-0">to</span>
            <input
              type="number"
              value={localPrice[1]}
              onChange={e => setLocalPrice([localPrice[0], Number(e.target.value)])}
              onBlur={() => set('maxPrice', localPrice[1])}
              placeholder="Max"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-yamass-accent"
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>৳{priceRange.min}</span>
            <span>৳{priceRange.max}</span>
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
